<template>
  <div id="app">
    <svg width="0" height="0" style="position: absolute; width: 0; height: 0">
      <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#d4af37" />
        <stop offset="100%" stop-color="#b8860b" />
      </linearGradient>
    </svg>
    <a href="tel:0670726791" class="stickyPhone">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="40"
        height="40"
        color="#ffff"
        fill="none"
      >
        <path
          d="M14 3V6M19 5L17 7M21 10H18"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M9.15825 5.71223L8.7556 4.80625C8.49232 4.21388 8.36068 3.91768 8.1638 3.69101C7.91707 3.40694 7.59547 3.19794 7.23567 3.08785C6.94858 3 6.62446 3 5.97621 3C5.02791 3 4.55375 3 4.15573 3.18229C3.68687 3.39702 3.26343 3.86328 3.09473 4.3506C2.95151 4.76429 2.99253 5.18943 3.07458 6.0397C3.94791 15.0902 8.90981 20.0521 17.9603 20.9254C18.8106 21.0075 19.2357 21.0485 19.6494 20.9053C20.1367 20.7366 20.603 20.3131 20.8177 19.8443C21 19.4462 21 18.9721 21 18.0238C21 17.3755 21 17.0514 20.9122 16.7643C20.8021 16.4045 20.5931 16.0829 20.309 15.8362C20.0823 15.6393 19.7861 15.5077 19.1937 15.2444L18.2878 14.8417C17.6462 14.5566 17.3255 14.4141 16.9995 14.3831C16.6876 14.3534 16.3731 14.3972 16.0811 14.5109C15.776 14.6297 15.5063 14.8544 14.967 15.3038C14.4301 15.7512 14.1617 15.9749 13.8337 16.0947C13.543 16.2009 13.1586 16.2403 12.8523 16.1951C12.5069 16.1442 12.2423 16.0029 11.7133 15.7201C10.0672 14.8405 9.15953 13.9328 8.27986 12.2867C7.99714 11.7577 7.85578 11.4931 7.80487 11.1477C7.75974 10.8414 7.79908 10.457 7.9053 10.1663C8.02512 9.83828 8.24881 9.56986 8.69619 9.033C9.14562 8.49368 9.37034 8.22402 9.48915 7.91891C9.60285 7.62694 9.64661 7.3124 9.61694 7.00048C9.58594 6.67452 9.44338 6.35376 9.15825 5.71223Z"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
        />
      </svg>
    </a>
    <Header />
    <router-view />
    <Footer />
  </div>
</template>

<script setup>
import { watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import Header from "./components/HeaderApp.vue";
import Footer from "./components/FooterApp.vue";
import { useStoreConfig } from "./services/storeService";
import { initPixels } from "./services/pixelService";

const { locale } = useI18n();
const { fetchStoreConfig } = useStoreConfig();

onMounted(async () => {
  const storeData = await fetchStoreConfig();
  if (storeData && storeData.pixels) {
    let pixelsList = storeData.pixels;
    if (typeof storeData.pixels === "string") {
      try {
        pixelsList = JSON.parse(storeData.pixels);
      } catch (e) {
        console.error("Failed to parse pixels:", e);
      }
    }
    initPixels(pixelsList);
  }
});

watch(
  locale,
  (val) => {
    document.documentElement.lang = val;
    document.dir = val === "ar" ? "rtl" : "ltr";
  },
  { immediate: true }
);
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@100;200;300;400;500;600;700&family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap");

:root {
  --primary: #0f172a; /* Deep, elegant dark blue */
  --secondary: #b89254; /* Luxurious gold */
  --accent: #9e7b46; /* Dark goldenrod for active states */
  --danger: #ef4444; /* Standard danger red */
  --dark: #1e293b; /* Sleek slate dark */
  --dark-color: #1e293b; /* Legacy support */
  --light: #fafafa; /* Crisp, clean off-white */
  --gray: #9ca3af; /* Neutral gray */
  --radius: 8px;
  --surface: #f1f5f9; /* Soft input background */
  --border: #e5e7eb; /* Soft border */ /* Modern, softer corners */
}

@keyframes magicalPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(212, 175, 55, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0);
  }
}

.magical-button {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.magical-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(212, 175, 55, 0.2);
}

html body {
  width: 100%;
  margin: 0;
  padding: 0;
  font-family: "IBM Plex Sans Arabic", "Ubuntu", sans-serif;
  background-color: var(--light);
  color: var(--dark);
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: var(--dark);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Global Minimalist Button Styles */
button {
  font-family: inherit;
}

.stickyPhone {
  position: fixed;
  bottom: 100px;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  right: 20px; /* Physical right */
  z-index: 9999;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.4);
  cursor: pointer;
  background-color: var(--secondary);
  color: white;
  transition: all 0.3s ease;
  margin: 0;
  animation: magicalPulse 2s infinite;
}

.stickyPhone:hover {
  transform: scale(1.1);
  background-color: var(--accent);
}

.stickyPhone svg {
  width: 24px;
  height: 24px;
}
</style>
